import { vidaClassicTheme } from '@vidahealth/vida-ui/themes';
import * as WebBrowser from 'expo-web-browser';
import { useColorScheme } from 'nativewind';
import { type ReactNode, useMemo } from 'react';
import { Platform, type TextStyle, Text } from 'react-native';
import RNMarkdown, { type ASTNode } from 'react-native-markdown-display';

// The markdown lib styles via a static `style` map (not nativewind `className`),
// so we resolve concrete values from the design system's tokens here rather
// than hardcoding hex/px — keeping it in lockstep with the design system.
//
// Read straight from vida-ui rather than from `@/modules/theme`'s
// `onboardingTheme`. That wrapper is `vidaClassicTheme.extend({ tokens: {} })` —
// an empty override — so its token tree is value-identical, and reaching for it
// would drag `modules/theme` into every graph that renders markdown, including
// the packaged `./dynamicAssessments` export. If onboarding ever does override a token, this
// is the line to revisit.
const { colors, typography, spacing } = vidaClassicTheme.tokens;
const { fontFamilies, fontSizes, fontSizeLineHeights } = typography;

// Token px strings ("16px") → RN numbers (16).
const px = (value: string) => parseInt(value, 10);
// Read a color-scale token (e.g. typography.700) for a color mode.
const colorToken = (mode: 'light' | 'dark', group: string, shade: string): string =>
  (colors[mode][group] as Record<string, string>)[shade];

// markdown heading level → vida-ui font-size token, following the design
// system's Heading element→size mapping (@vidahealth/vida-ui Heading renders
// size 3xl→<h1>, 2xl→<h2>, xl→<h3>, lg→<h4>, md→<h5>, sm→<h6>, i.e.
// text-4xl/3xl/2xl/xl/lg/base). The lib's own defaults make h5/h6 smaller than
// the body, so a backend `##### Title` would otherwise render as tiny text.
const HEADING_FONT_SIZE_TOKEN = {
  heading1: '4xl',
  heading2: '3xl',
  heading3: '2xl',
  heading4: 'xl',
  heading5: 'lg',
  heading6: 'base',
} as const;

// Open an external link the way the rest of onboarding does (see openPolicy):
// a new tab on web, an in-app browser on native. Returning false tells the
// markdown lib we've handled it, so it doesn't also call Linking.openURL.
function openLink(url: string): false {
  if (url) {
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      void WebBrowser.openBrowserAsync(url);
    }
  }
  return false;
}

/**
 * Renders a markdown string with vida-ui styling. Use wherever backend copy
 * arrives as markdown (e.g. state-compliance disclaimers, which embed links to
 * attorneys-general info / opt-out forms) instead of a plain `<Text>`, which
 * would surface the raw `[label](url)` syntax to the user.
 */
export function Markdown({ children }: { children: string }) {
  const { colorScheme } = useColorScheme();
  const mode = colorScheme === 'dark' ? 'dark' : 'light';

  const styles = useMemo(() => {
    // headings: typography-900 + the heading-bold font (vida-ui's
    // `text-typography-900 font-heading-bold`), sized per the token above.
    const heading = (sizeToken: string) => ({
      color: colorToken(mode, 'typography', '900'),
      fontFamily: fontFamilies.headingSemibold,
      fontSize: px(fontSizes[sizeToken]),
      lineHeight: px(fontSizeLineHeights[sizeToken]),
      marginBottom: px(spacing['2']),
    });
    return {
      body: {
        color: colorToken(mode, 'typography', '700'),
        fontFamily: fontFamilies.body,
        fontSize: px(fontSizes.base),
        lineHeight: px(fontSizeLineHeights.base),
      },
      heading1: heading(HEADING_FONT_SIZE_TOKEN.heading1),
      heading2: heading(HEADING_FONT_SIZE_TOKEN.heading2),
      heading3: heading(HEADING_FONT_SIZE_TOKEN.heading3),
      heading4: heading(HEADING_FONT_SIZE_TOKEN.heading4),
      heading5: heading(HEADING_FONT_SIZE_TOKEN.heading5),
      heading6: heading(HEADING_FONT_SIZE_TOKEN.heading6),
      strong: { fontFamily: fontFamilies.bodySemibold },
      link: { color: colorToken(mode, 'info', '700'), textDecorationLine: 'underline' as const },
    };
  }, [mode]);

  // The lib's default heading renderers wrap text in a <View> with no semantic
  // role, so on web a `##### Title` is just styled <div>s. Render headings as a
  // <Text accessibilityRole="header" aria-level={n}> instead: react-native-web
  // turns that into a real <h1>–<h6>, and (being a Text, not a View) the heading
  // font styling actually applies to the text.
  const rules = useMemo(() => {
    // These are react-native-markdown-display render rules, not React components,
    // so the display-name rule doesn't apply.
    const headingRule =
      (level: 1 | 2 | 3 | 4 | 5 | 6, style: TextStyle) =>
      // eslint-disable-next-line react/display-name
      (node: ASTNode, children: ReactNode) => (
        <Text key={node.key} accessibilityRole='header' aria-level={level} style={style}>
          {children}
        </Text>
      );
    return {
      heading1: headingRule(1, styles.heading1),
      heading2: headingRule(2, styles.heading2),
      heading3: headingRule(3, styles.heading3),
      heading4: headingRule(4, styles.heading4),
      heading5: headingRule(5, styles.heading5),
      heading6: headingRule(6, styles.heading6),
    };
  }, [styles]);

  return (
    <RNMarkdown style={styles} rules={rules} onLinkPress={openLink}>
      {children}
    </RNMarkdown>
  );
}
